import type { ComponentProps } from "react";
import { t } from "@i18n";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";

/**
 * A form-template section, as far as this validator is concerned. Structural rather than the
 * server's `FormTemplateField`, because FileUpload hands in sections whose `questionFields` it
 * has re-mapped with populated answers.
 */
interface ValidatableSection {
  questionFields?: { _id?: string; answer?: { value?: unknown } }[];
}

/** The one member used off `useFormValidator`'s return value. */
type FieldValidator = { validate: (rule: { name: string; value: unknown }) => boolean };

const useCustomFields = () => {
  const validateFormWithId = (
    id: string | number,
    template: ValidatableSection[] | undefined,
    formValidator: FieldValidator | undefined,
  ) => {
    const rules: { name: string; value: unknown }[] = [];
    if (!template || !template.length) {
      return true;
    }
    template.forEach((section) => {
      // KNOWN BUG, preserved verbatim: a section with no `questionFields` (the field is optional
      // on the template schema) throws here. The `!` is erased; a guard would change behaviour.
      section.questionFields!.forEach((item) => {
        rules.push({ name: `${id}_${item._id}`, value: item.answer?.value });
      });
    });
    // `boolean | undefined`, not `boolean`: `formValidator` is optional at both call sites, so the
    // optional call below yields `undefined` when no validator was supplied.
    let valid: boolean | undefined = true;
    rules.forEach((rule) => {
      const ruleValid = formValidator?.validate({
        name: rule.name,
        value: rule.value,
      });
      valid = valid && ruleValid;
    });
    return valid;
  };

  return {
    validateFormWithId,
  };
};

/**
 * One entry of a folder's `fileNomenclature.fields[]`. `_id` / `name` are declared because the
 * callers' own narrower row types mention only those, and an all-optional type with nothing in
 * common would trip TS's weak-type check against them.
 */
interface NomenclatureFieldDef {
  _id?: string;
  name?: string;
  type?: string;
  allowedCharacters?: number;
  options?: readonly (string | { text?: string; value?: string })[];
}

interface NomenclatureFieldProps {
  /** The row this field belongs to; only `id` is read, the rest is forwarded to the change handler. */
  args: { id: string | number; [key: string]: unknown };
  field: NomenclatureFieldDef;
  index: number;
  value: string;
  /**
   * LEFT AS `any`. RE-ATTEMPTED with method shorthand, and the shorthand DOES work for the two
   * external consumers — a probe confirmed both useFileManager's `(e: unknown, args: { value?;
   * itemData? }, …)` and FilesSlickGrid's `(e: React.ChangeEvent<HTMLInputElement>, args: { id:
   * number }, …)` satisfy a bivariant slot. The blocker is NOT those consumers.
   *
   * It is this module's own two call sites: line ~121 forwards a
   * `ChangeEvent<HTMLInputElement | HTMLTextAreaElement>` (broader than either consumer declares),
   * and line ~163 synthesises `{ target: { value } }`, a plain object with no `EventTarget`. A
   * declared `e` must therefore be loose enough for both of those AND survive bivariance against the
   * consumers — plausibly `e: { target?: { value?: string } }` with every payload member optional,
   * which is the shape that worked elsewhere. Not landed here: the first concrete attempt regressed
   * this file by 2 errors, and reverting was preferred over shipping an unverified second attempt.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- this module's own call sites forward a broader ChangeEvent; see comment above
  handleNomenclatureChange: any;
}

export const getNomenclatureFieldWidthPx = (field: NomenclatureFieldDef | undefined) => {
  if (field?.type === "single select") return 200;
  const allowed = Number(field?.allowedCharacters) || 0;
  return Math.max(110, allowed * 10 + 28);
};

export const getNomenclatureFieldWidth = (field: NomenclatureFieldDef | undefined) =>
  `${getNomenclatureFieldWidthPx(field) / 16}rem`;

export const NomenclatureField = ({ args, field, index, value, handleNomenclatureChange }: NomenclatureFieldProps) => {
  if (field.type == "text" || field.type == "number") {
    const char = field.type == "text" ? "A" : "1";
    const placeholderText = field.allowedCharacters ? char.repeat(field.allowedCharacters) : `${char.repeat(3)}...`;
    // Dead: built here but never passed to the input below. Left verbatim.
    const htmlAttributes: Record<string, string> = {};
    if (field.allowedCharacters) {
      htmlAttributes.maxlength = `${field.allowedCharacters}`;
    }
    const allowedPattern = field.type == "text" ? /^[A-Za-z]*$/ : /^[0-9]*$/;
    const handleChange: NonNullable<ComponentProps<typeof TextFieldComponent>["onChange"]> = (e) => {
      const newValue = e.target.value;
      const maxLength = field.allowedCharacters;
      if (maxLength && newValue.length > maxLength) {
        return;
      }
      if (newValue !== "" && !allowedPattern.test(newValue)) {
        return;
      }
      handleNomenclatureChange(e, args, field, index);
    };

    return (
      <div className="shrink-0" style={{ width: getNomenclatureFieldWidth(field) }}>
        <TextFieldComponent
          id={`${args.id}-${field.type}-${index}`}
          type="text"
          onClick={(e: React.SyntheticEvent) => e.stopPropagation()}
          value={value}
          placeholder={placeholderText}
          slotProps={{
            htmlInput: {
              maxLength: field.allowedCharacters,
              inputMode: field.type == "number" ? "numeric" : "text",
            },
          }}
          onChange={handleChange}
        />
      </div>
    );
  }
  if (field.type == "single select") {
    // field.options can be array of strings or array of {text, value} objects
    // Both `!`s restate the optional-chained guard; they are erased and keep `> 0` verbatim.
    const isStringArray = field.options?.length! > 0 && typeof field.options![0] === "string";

    return (
      <div className="shrink-0" style={{ width: getNomenclatureFieldWidth(field) }}>
        <AutoComplete
          id={`${args.id}-${field.type}-${index}`}
          key={`${args.id}-${field.type}-${index}`}
          width="100%"
          options={field.options || []}
          valueField={isStringArray ? undefined : "value"}
          getOptionLabel={(option) => {
            if (typeof option === "string") return option;
            return option.text || "";
          }}
          value={value}
          onChange={(_event, newValue) => {
            const selectedValue = typeof newValue === "string" ? newValue : newValue?.value;
            handleNomenclatureChange({ target: { value: selectedValue ?? "" } }, args, field, index);
          }}
          renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Select an option")} />}
        />
      </div>
    );
  }
};
export const getNomenclatureField = (
  args: NomenclatureFieldProps["args"],
  field: NomenclatureFieldDef,
  index: number,
  value: string,
  handleNomenclatureChange: NomenclatureFieldProps["handleNomenclatureChange"],
) => (
  <NomenclatureField
    args={args}
    field={field}
    index={index}
    value={value}
    handleNomenclatureChange={handleNomenclatureChange}
  />
);

NomenclatureField.defaultProps = {
  value: "",
};
export default useCustomFields;
